/**
 * Health API Service
 * Handles bridge health check operations.
 *
 * Base URL: /api (apiFetch adds /api prefix)
 */
import { useCallback, useMemo } from "react";
import { useApi } from "@/hooks/use-api";
import type { BackendVersionDto, BridgeHeartbeatResponseDto } from "@/types/api/health";

const HEALTH_BASE = "/health";

/**
 * Hook providing health API operations with authentication
 */
export function useHealthApi() {
    const api = useApi();

    /**
     * Get bridge heartbeat status for the authenticated user's bridge
     * GET /health/bridge
     */
    const getBridgeHealth = useCallback(async (): Promise<BridgeHeartbeatResponseDto> => {
        return api.get<BridgeHeartbeatResponseDto>(`${HEALTH_BASE}/bridge`);
    }, [api]);

    /**
     * Get the deployed backend version (CI-stamped file version + informational version)
     * GET /version
     */
    const getBackendVersion = useCallback(async (): Promise<BackendVersionDto> => {
        return api.get<BackendVersionDto>("/version");
    }, [api]);

    return useMemo(
        () => ({
            getBridgeHealth,
            getBackendVersion,
        }),
        [getBridgeHealth, getBackendVersion],
    );
}
